import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import ReplenishDialog from './ReplenishDialog';
import { Button } from './ui/button';
import { ArrowsLeftRight, Warning, ArrowRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { wsEventsUrl } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

// Events that can change on-hand / reservations and therefore the bottom-N
// low-stock snapshot. Picking/packing/shipping consume stock; transfers +
// receipts add or move it; internal inventory_transferred is our own
// replenishment signal.
const STOCK_EVENTS = new Set([
  'pick_completed',
  'pack_completed',
  'order_shipped',
  'receipt_created',
  'transfer_created',
  'inventory_transferred',
]);

/**
 * Low-Stock Alerts ticker — warehouse-wide view of the N positions with the
 * worst net_available. Each row shows SKU, location, on_hand, reserved, net,
 * and a one-click Replenish trigger that opens the shared <ReplenishDialog>.
 *
 * Surfaces only when the API returns ≥ 1 alert — stays invisible when the
 * warehouse is healthy so the dashboard doesn't shout for attention
 * unnecessarily.
 */
export default function LowStockTicker({ limit = 5, warehouseId = null, titleSuffix = '' }) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState(null); // null = loading, [] = healthy
  const [err, setErr] = useState(null);
  const [active, setActive] = useState(null); // row opened in dialog

  const fetchAlerts = useCallback(async () => {
    setErr(null);
    try {
      const params = { limit };
      if (warehouseId) params.warehouse_id = warehouseId;
      const { data } = await axios.get(`${API}/api/lookups/low-stock-alerts`, {
        params,
        withCredentials: true,
      });
      setAlerts(data.alerts || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load low-stock alerts');
      setAlerts([]); // fail-closed — don't block the dashboard
    }
  }, [limit, warehouseId]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  // Live refresh — subscribe to the global event bus so the ticker reflects
  // picker/packer/shipping activity as it happens. Debounced to coalesce
  // bursts of events (e.g. a big pick list completing emits many events).
  const debounceRef = useRef(null);
  const [liveTick, setLiveTick] = useState(0);
  useEffect(() => {
    let ws;
    let reconnectTimer;
    const connect = () => {
      try {
        ws = new WebSocket(wsEventsUrl());
        ws.onmessage = (ev) => {
          let evt;
          try { evt = JSON.parse(ev.data); } catch (_) { return; }
          if (!STOCK_EVENTS.has(evt?.type)) return;
          // Debounce: coalesce bursts into a single refetch within 1200ms
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            fetchAlerts();
            setLiveTick((t) => t + 1);
          }, 1200);
        };
        ws.onclose = () => {
          reconnectTimer = setTimeout(connect, 5000);
        };
        ws.onerror = () => { /* onclose will reconnect */ };
      } catch (_) { /* ignore */ }
    };
    connect();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) { ws.onclose = null; try { ws.close(); } catch (_) { /* noop */ } }
    };
  }, [fetchAlerts]);

  // Loading placeholder (lets the dashboard lay out predictably)
  if (alerts === null) {
    return (
      <div className="mb-6 border border-border bg-card p-3 animate-pulse" data-testid="low-stock-ticker-loading">
        <div className="h-4 w-40 bg-muted" />
      </div>
    );
  }

  // Healthy state — collapse to nothing
  if (alerts.length === 0 && !err) return null;

  return (
    <div className="mb-6 border border-amber-300 bg-amber-50" data-testid="low-stock-ticker">
      <div className="flex items-center justify-between px-4 py-2 border-b border-amber-200">
        <div className="flex items-center gap-2">
          <Warning size={18} weight="fill" className="text-amber-600" />
          <span className="text-xs font-bold uppercase tracking-widest text-amber-800">
            Low-stock alerts{titleSuffix ? ` · ${titleSuffix}` : ''} · {alerts.length} need attention
          </span>
          {liveTick > 0 && (
            <span
              data-testid="low-stock-ticker-live"
              className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700"
              title="Updating live from operations stream"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live
            </span>
          )}
        </div>
        <button
          data-testid="low-stock-ticker-goto-lookup"
          onClick={() => navigate('/lookup')}
          className="text-[10px] font-bold uppercase tracking-widest text-amber-800 hover:text-amber-900 flex items-center gap-1"
        >
          Open lookup <ArrowRight size={12} weight="bold" />
        </button>
      </div>
      <ul className="divide-y divide-amber-100">
        {alerts.map((a) => (
          <li
            key={`${a.variant_id}-${a.location_id}`}
            data-testid={`low-stock-row-${a.sku_code}-${a.location_code}`}
            className="flex items-center gap-3 px-4 py-2 hover:bg-amber-100/50 text-sm"
          >
            <div className="flex-1 flex items-center gap-3 min-w-0">
              <span className="font-mono text-xs font-bold text-foreground shrink-0">{a.sku_code || '—'}</span>
              <span className="text-muted-foreground truncate">{a.sku_name || '—'}</span>
              <span className="text-slate-300">@</span>
              <span className="font-mono text-xs font-bold text-slate-800 shrink-0">{a.location_code}</span>
              {a.zone && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Zone {a.zone}</span>}
            </div>
            <div className="flex items-center gap-4 font-mono text-xs shrink-0">
              <span className="text-muted-foreground">on-hand <span className="font-bold text-slate-800">{a.on_hand_available}</span></span>
              <span className="text-amber-700">reserved <span className="font-bold">{a.on_hand_reserved}</span></span>
              <span className="text-emerald-700">net <span className="font-bold">{a.net_available}</span></span>
            </div>
            {a.has_source ? (
              <Button
                data-testid={`low-stock-replenish-${a.sku_code}-${a.location_code}`}
                size="sm"
                onClick={() => setActive(a)}
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-none font-bold text-[10px] uppercase tracking-widest shrink-0"
              >
                <ArrowsLeftRight size={12} weight="bold" className="mr-1" />
                Replenish
              </Button>
            ) : (
              <span
                data-testid={`low-stock-no-source-${a.sku_code}-${a.location_code}`}
                className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0"
              >
                no source
              </span>
            )}
          </li>
        ))}
      </ul>
      {err && (
        <div data-testid="low-stock-ticker-error" className="px-4 py-2 text-xs text-rose-700 bg-rose-50 border-t border-rose-200">
          {err}
        </div>
      )}

      {active && (
        <ReplenishDialog
          row={{
            variant_id: active.variant_id,
            sku_code: active.sku_code,
            on_hand_available: active.on_hand_available,
            on_hand_reserved: active.on_hand_reserved,
          }}
          targetLocation={{ id: active.location_id, location_code: active.location_code }}
          onClose={() => setActive(null)}
          onDone={() => { setActive(null); fetchAlerts(); }}
        />
      )}
    </div>
  );
}
